import { useMemo, useState } from 'react';
import { useAnonymisePerson, useInviteTeammate, usePersonSessions, useRevokeSessions, useStudioPeople, useStudioTeam, useUpdatePerson } from '@/api/studio';
import type { ClientRole, PersonAccess, StudioRole, TeamMember } from '@/api/types';
import { PersonLink } from '@/components/PersonDialog';
import { QueryPending } from '@/components/QueryState';
import { Avatar, Dialog, Empty, PageHeader, Section, Status, Tabs } from '@/components/ui';
import { tabPanel } from '@/lib/tabs';
import { clientRoleLabel, studioCan, studioRoleLabel } from '@/lib/access';
import { formatWhen } from '@/lib/format';
import { Link } from 'react-router-dom';

const studioRoles = Object.keys(studioRoleLabel) as StudioRole[];
const clientRoles = Object.keys(clientRoleLabel) as ClientRole[];

function AccessState({ invited, disabled }: { invited?: string; disabled?: boolean }) {
  if (disabled) return <Status tone="overdue">отключён</Status>;
  if (invited === 'pending') return <Status tone="waiting">приглашён</Status>;
  return <Status tone="done">в кабинете</Status>;
}

const seen = (iso?: string | null) => (iso ? formatWhen(iso) : '—');

/** Disable / enable and «end sessions» for one person, with the server's refusal shown inline. */
function Actions({ id, disabled, onError }: { id: string; disabled: boolean; onError: (m: string) => void }) {
  const update = useUpdatePerson();
  const revoke = useRevokeSessions();
  const fail = (e: Error) => onError(e.message);
  return (
    <div className="flex justify-end gap-1">
      <button className="btn-ghost h-7 px-2 text-xs" disabled={revoke.isPending || disabled} onClick={() => revoke.mutate(id, { onError: fail })}>
        Завершить сессии
      </button>
      <button className="btn-ghost h-7 px-2 text-xs" disabled={update.isPending} onClick={() => update.mutate({ id, patch: { disabled: !disabled } }, { onError: fail })}>
        {disabled ? 'Вернуть доступ' : 'Отключить'}
      </button>
    </div>
  );
}

function Team({ onError }: { onError: (m: string) => void }) {
  const teamQuery = useStudioTeam();
  const team = teamQuery.data;
  const update = useUpdatePerson();
  const invite = useInviteTeammate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<StudioRole>('specialist');
  if (!team) return <QueryPending query={teamQuery} />;
  return (
    <>
      <ul className="divide-y divide-line border-y border-line">
        {team.map((p: TeamMember) => (
          <li key={p.id} className="grid grid-cols-1 items-center gap-3 py-3 sm:grid-cols-[1fr_160px_120px_110px_auto]">
            <span className="flex min-w-0 items-center gap-3">
              <Avatar person={p} />
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium">
                  <PersonLink person={p} />
                </span>
                <span className="block truncate text-xs text-muted">{p.email}</span>
              </span>
            </span>
            <select
              className="field h-8 text-sm"
              aria-label={`Роль: ${p.name}`}
              value={p.studioRole}
              onChange={(e) => update.mutate({ id: p.id, patch: { studioRole: e.target.value as StudioRole } }, { onError: (err) => onError(err.message) })}
            >
              {studioRoles.map((r) => (
                <option key={r} value={r}>
                  {studioRoleLabel[r]}
                </option>
              ))}
            </select>
            <AccessState invited={p.invited} disabled={p.disabled} />
            <span className="text-xs text-muted">{seen(p.lastSeen)}</span>
            <Actions id={p.id} disabled={!!p.disabled} onError={onError} />
          </li>
        ))}
      </ul>
      <form
        className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_180px_auto]"
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim() || !/.+@.+\..+/.test(email)) return;
          invite.mutate({ name: name.trim(), email: email.trim(), studioRole: role }, { onSuccess: () => (setName(''), setEmail('')), onError: (err) => onError(err.message) });
        }}
      >
        <input className="field" placeholder="Имя" value={name} onChange={(e) => setName(e.target.value)} />
        <input className="field" placeholder="Почта" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <select className="field" aria-label="Роль нового человека" value={role} onChange={(e) => setRole(e.target.value as StudioRole)}>
          {studioRoles.map((r) => (
            <option key={r} value={r}>
              {studioRoleLabel[r]}
            </option>
          ))}
        </select>
        <button className="btn-primary" disabled={invite.isPending}>
          Пригласить
        </button>
      </form>
      <p className="mt-2 text-xs text-muted">Владелец — всё, включая команду и журнал. Менеджер — свои клиенты и проекты. Специалист — только свои проекты, без денег.</p>
    </>
  );
}

/** One person: fix the name or e-mail, see the sessions (no tokens), anonymise after a second confirmation. */
function PersonDialog({ person, onClose, onError }: { person: PersonAccess; onClose: () => void; onError: (m: string) => void }) {
  const update = useUpdatePerson();
  const anonymise = useAnonymisePerson();
  const sessionsQuery = usePersonSessions(person.person.id, true);
  const sessions = sessionsQuery.data;
  const [name, setName] = useState(person.person.name);
  const [email, setEmail] = useState(person.email);
  const [confirming, setConfirming] = useState(false);
  const [problem, setProblem] = useState('');
  const fail = (e: Error) => setProblem(e.message);
  const changed = name.trim() !== person.person.name || email.trim().toLowerCase() !== person.email;
  return (
    <>
      <Dialog open={!confirming} onClose={onClose} title={`Человек: ${person.person.name}`}>
        <div className="space-y-5 p-5 text-sm">
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              setProblem('');
              update.mutate({ id: person.person.id, patch: { name: name.trim(), email: email.trim() } }, { onError: fail });
            }}
          >
            <label className="block">
              <span className="mb-1 block text-muted">Имя</span>
              <input className="field" name="name" maxLength={200} value={name} onChange={(e) => setName(e.target.value)} />
            </label>
            <label className="block">
              <span className="mb-1 block text-muted">Почта</span>
              <input className="field" name="email" type="email" maxLength={254} value={email} onChange={(e) => setEmail(e.target.value)} />
            </label>
            <button className="btn-primary" disabled={!changed || update.isPending}>
              Сохранить
            </button>
          </form>
          <div>
            <h3 className="mb-2 font-medium">Сессии</h3>
            {!sessions ? (
              <QueryPending query={sessionsQuery} compact />
            ) : sessions.length === 0 ? (
              <p className="text-muted">Открытых сессий нет.</p>
            ) : (
              <ul className="divide-y divide-line border-y border-line text-xs">
                {sessions.map((s) => (
                  <li key={s.createdAt + s.lastSeenAt} className="grid grid-cols-3 gap-2 py-2">
                    <span>вошёл {formatWhen(s.createdAt)}</span>
                    <span>был {formatWhen(s.lastSeenAt)}</span>
                    <span>до {formatWhen(s.expiresAt)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {problem && (
            <p role="alert" className="text-overdue">
              {problem}
            </p>
          )}
          <div className="border-t border-line pt-4">
            <button className="btn-ghost text-overdue" onClick={() => setConfirming(true)}>
              Обезличить
            </button>
          </div>
        </div>
      </Dialog>
      <Dialog open={confirming} onClose={() => setConfirming(false)} title="Обезличить человека?">
        <div className="space-y-4 p-5 text-sm">
          <p>
            Имя и почта будут заменены, доступ закрыт, сессии завершены. Сообщения останутся и будут подписаны «Бывший сотрудник клиента». Отменить это нельзя.
          </p>
          <div className="flex gap-2">
            <button
              className="btn-primary"
              disabled={anonymise.isPending}
              onClick={() =>
                anonymise.mutate(person.person.id, {
                  onSuccess: onClose,
                  onError: (e) => (onError(e.message), setConfirming(false)),
                })
              }
            >
              Да, обезличить
            </button>
            <button className="btn-ghost" onClick={() => setConfirming(false)}>
              Отмена
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
}

type Side = 'all' | 'studio' | 'client';

function People({ onError }: { onError: (m: string) => void }) {
  const peopleQuery = useStudioPeople();
  const data = peopleQuery.data;
  const update = useUpdatePerson();
  const [side, setSide] = useState<Side>('all');
  const [client, setClient] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const clientNames = useMemo(() => [...new Set((data ?? []).map((p) => p.clientName).filter((n): n is string => !!n))].sort(), [data]);
  if (!data) return <QueryPending query={peopleQuery} />;
  const opened = data.find((p) => p.person.id === openId);
  const rows = data.filter((p) => (side === 'all' || p.side === side) && (!client || p.clientName === client));
  const roleCell = (p: PersonAccess) =>
    p.side === 'client' ? (
      <select
        className="field h-8 text-sm"
        aria-label={`Роль: ${p.person.name}`}
        value={p.clientRole ?? 'member'}
        onChange={(e) => update.mutate({ id: p.person.id, patch: { clientRole: e.target.value as ClientRole } }, { onError: (err) => onError(err.message) })}
      >
        {clientRoles.map((r) => (
          <option key={r} value={r}>
            {clientRoleLabel[r]}
          </option>
        ))}
      </select>
    ) : (
      <span className="text-sm">{p.studioRole ? studioRoleLabel[p.studioRole] : '—'}</span>
    );
  return (
    <>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <Tabs
          id="team"
          label="Кто в списке"
          value={side}
          onChange={setSide}
          items={[
            { value: 'all', label: 'Все', count: data.length },
            { value: 'studio', label: 'Студия', count: data.filter((p) => p.side === 'studio').length },
            { value: 'client', label: 'Клиенты', count: data.filter((p) => p.side === 'client').length },
          ]}
        />
        {side !== 'studio' && (
          <select className="field mb-6 h-8 w-56 text-sm" aria-label="Клиент" value={client} onChange={(e) => setClient(e.target.value)}>
            <option value="">Все клиенты</option>
            {clientNames.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        )}
      </div>
      <div {...tabPanel('team', side)} className="focus:outline-none">
      {rows.length === 0 ? (
        <Empty title="Никого не нашлось" />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead className="text-xs text-muted">
              <tr className="border-b border-line">
                <th className="py-2 font-normal">Человек</th>
                <th className="py-2 font-normal">Клиент</th>
                <th className="py-2 font-normal">Роль</th>
                <th className="py-2 font-normal">Статус</th>
                <th className="py-2 font-normal">Последний вход</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {rows.map((p) => (
                <tr key={p.person.id}>
                  <td className="py-2.5 pr-3">
                    <span className="block font-medium">{p.person.name}</span>
                    <span className="block text-xs text-muted">{p.email}</span>
                  </td>
                  <td className="py-2.5 pr-3 text-muted">{p.side === 'studio' ? 'Студия' : p.clientName}</td>
                  <td className="w-44 py-2.5 pr-3">{roleCell(p)}</td>
                  <td className="py-2.5 pr-3">
                    <AccessState invited={p.invited} disabled={p.disabled} />
                  </td>
                  <td className="py-2.5 pr-3 text-xs text-muted">{seen(p.lastSeen)}</td>
                  <td className="py-2.5">
                    <div className="flex items-center justify-end gap-1">
                      <button className="btn-ghost h-7 px-2 text-xs" aria-label={`Изменить: ${p.person.name}`} onClick={() => setOpenId(p.person.id)}>
                        Изменить
                      </button>
                      <Actions id={p.person.id} disabled={p.disabled} onError={onError} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </div>
      {opened && <PersonDialog key={opened.person.id} person={opened} onClose={() => setOpenId(null)} onError={onError} />}
    </>
  );
}

/** Owners only (the server answers 403 to anyone else): the team, everyone's access and the audit log. */
export function StudioTeam() {
  const team = useStudioTeam();
  const me = team.data?.[0];
  const [error, setError] = useState('');
  if (!me) return <QueryPending query={team} />;
  if (!studioCan(me.studioRole, 'team')) return <Empty title="Раздел для владельцев студии">Команду и доступы настраивает владелец студии.</Empty>;
  return (
    <>
      <PageHeader title="Команда и доступы" description="Кто работает в кабинете, что каждому можно, а журнал действий — в разделе «Администрирование»." />
      {error && (
        <p role="alert" className="mb-6 text-sm text-overdue">
          {error}{' '}
          <button className="underline underline-offset-2" onClick={() => setError('')}>
            Понятно
          </button>
        </p>
      )}
      <Section title="Команда студии">
        <Team onError={setError} />
      </Section>
      <Section title="Все люди">
        <People onError={setError} />
      </Section>
      <Section title="Журнал">
        <p className="text-sm text-muted">
          Журнал входов и изменений —{' '}
          <Link className="underline underline-offset-2 hover:text-ink" to="/studio/admin/audit">
            Администрирование → Журнал действий
          </Link>
          .
        </p>
      </Section>
    </>
  );
}
